// Contratos (schemas/DTOs) da fatia "finance-reports": finanças (caixa, lançamentos), relatórios e exportações.
import { z } from 'zod';
import { dateKey, paginationQuery, periodQuery } from '../schemas/common';
import type { CashSessionDTO, FinanceEntryDTO, Paginated, SalesReportDTO } from '../types';

// ============================================================================
// Consultas (query strings)
// ============================================================================

/** Granularidade da série do relatório de vendas. `auto`: hora (1 dia), dia (até 62), semana (até 180), mês. */
export const REPORT_GRANULARITIES = ['auto', 'hour', 'day', 'week', 'month'] as const;
export type ReportGranularity = (typeof REPORT_GRANULARITIES)[number];

export const salesReportQuery = periodQuery.extend({
  granularity: z.enum(REPORT_GRANULARITIES).default('auto'),
});
export type SalesReportQuery = z.infer<typeof salesReportQuery>;

export const productRankingQuery = periodQuery.extend({
  sort: z.enum(['revenue', 'quantity']).default('revenue'),
  /** Sem o recurso "Estatísticas avançadas" a API devolve no máximo 5 itens. */
  limit: z.coerce.number().int().min(1).max(100).default(10),
});
export type ProductRankingQuery = z.infer<typeof productRankingQuery>;

export const customerRankingQuery = periodQuery.extend({
  metric: z.enum(['revenue', 'orders']).default('revenue'),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type CustomerRankingQuery = z.infer<typeof customerRankingQuery>;

export const stockInsightsQuery = z.object({
  /** Janela de análise em dias (padrão 60). */
  days: z.coerce.number().int().min(7).max(365).default(60),
  /** Máximo de linhas em cada lista. */
  limit: z.coerce.number().int().min(1).max(500).default(50),
});
export type StockInsightsQuery = z.infer<typeof stockInsightsQuery>;

export const cashSessionListQuery = paginationQuery.extend({
  status: z.enum(['open', 'closed']).optional(),
  from: dateKey.optional(),
  to: dateKey.optional(),
});
export type CashSessionListQuery = z.infer<typeof cashSessionListQuery>;

/** Exclusão de lançamento: `one` só este; `future` este e as próximas parcelas; `all` todas as parcelas do grupo. */
export const deleteFinanceEntryQuery = z.object({
  scope: z.enum(['one', 'future', 'all']).default('one'),
});
export type DeleteFinanceEntryScope = z.infer<typeof deleteFinanceEntryQuery>['scope'];

// ============================================================================
// Exportações
// ============================================================================

export const EXPORT_TYPES = [
  'sales',
  'sales-items',
  'products',
  'stock',
  'customers',
  'finance-entries',
  'cash-sessions',
  'fiado',
] as const;
export type ExportType = (typeof EXPORT_TYPES)[number];

export const EXPORT_FORMATS = ['csv', 'xlsx', 'pdf'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];

export const EXPORT_TYPE_META: Record<ExportType, { label: string; description: string; filename: string; usesPeriod: boolean }> = {
  sales: { label: 'Vendas', description: 'Uma linha por venda, com cliente, vendedor, itens e formas de pagamento.', filename: 'vendas', usesPeriod: true },
  'sales-items': { label: 'Itens vendidos', description: 'Uma linha por produto vendido.', filename: 'itens-vendidos', usesPeriod: true },
  products: { label: 'Produtos', description: 'Cadastro de produtos com preços e estoque.', filename: 'produtos', usesPeriod: false },
  stock: { label: 'Estoque', description: 'Saldo de estoque por produto e variação.', filename: 'estoque', usesPeriod: false },
  customers: { label: 'Clientes', description: 'Clientes com compras e saldo de fiado.', filename: 'clientes', usesPeriod: false },
  'finance-entries': { label: 'Lançamentos financeiros', description: 'Contas a pagar e a receber (por vencimento).', filename: 'lancamentos', usesPeriod: true },
  'cash-sessions': { label: 'Fechamentos de caixa', description: 'Aberturas, sangrias, suprimentos e conferência do caixa.', filename: 'caixas', usesPeriod: true },
  fiado: { label: 'Movimentos de fiado', description: 'Compras fiado, pagamentos e ajustes na conta dos clientes.', filename: 'fiado', usesPeriod: true },
};

export const EXPORT_FORMAT_LABELS: Record<ExportFormat, string> = {
  csv: 'CSV (planilha simples)',
  xlsx: 'Excel (.xlsx)',
  pdf: 'PDF',
};

export const exportQuery = periodQuery.extend({
  format: z.enum(EXPORT_FORMATS).default('xlsx'),
});
export type ExportQuery = z.infer<typeof exportQuery>;

// ============================================================================
// DTOs
// ============================================================================

/** GET /finance/entries — lista paginada + totais de TODO o filtro (não só da página). */
export interface FinanceEntryListDTO extends Paginated<FinanceEntryDTO> {
  totals: { incomeCents: number; expenseCents: number };
}

/** POST /finance/entries — lançamento único ou parcelas mensais (mesmo groupId). */
export interface FinanceEntryCreateResultDTO {
  entries: FinanceEntryDTO[];
  groupId: string | null;
}

/**
 * Caixa com totais detalhados. Extende CashSessionDTO com os recebimentos de fiado
 * (entram no dinheiro esperado quando recebidos em dinheiro).
 * expectedCashCents = abertura + vendas em dinheiro + fiado recebido em dinheiro + suprimentos − sangrias
 */
export type CashSessionDetailDTO = CashSessionDTO & {
  totals: {
    openingAmountCents: number;
    /** Recebimentos de fiado (todas as formas) entre a abertura e o fechamento/agora. */
    fiadoReceivedCents: number;
    /** Parte dos recebimentos de fiado feita em dinheiro. */
    fiadoReceivedCashCents: number;
    /** Taxas de maquininha/meios de pagamento das vendas do caixa (informativo). */
    feesCents: number;
  };
};

/**
 * GET /reports/sales. Igual a SalesReportDTO, mas `previous` é null (e heatmap vazio,
 * custo/lucro null) quando a loja não tem o recurso "Estatísticas avançadas".
 */
export type SalesReportResponseDTO = Omit<SalesReportDTO, 'previous'> & {
  previous: SalesReportDTO['previous'] | null;
  /** true quando o plano inclui "Estatísticas avançadas" (reportsAdvanced). */
  advanced: boolean;
};

/** GET /reports/stock-insights — produtos parados, giro por variação/tamanho e sugestão de reposição. */
export interface StockInsightsDTO {
  days: number;
  from: string;
  to: string;
  /** Produtos com estoque e sem nenhuma venda na janela. */
  stalled: {
    productId: string;
    name: string;
    stock: number;
    unit: string;
    stockValueCents: number;
    costValueCents: number | null;
    lastSaleAt: string | null;
    daysWithoutSale: number | null;
  }[];
  stalledTotals: { products: number; units: number; valueCents: number; costCents: number | null };
  /** Giro por variação: vendido na janela vs. estoque atual. */
  turnover: {
    productId: string;
    variantId: string | null;
    name: string;
    variantName: string | null;
    soldQty: number;
    stock: number;
    /** vendido / (vendido + estoque) × 100 */
    sellThroughPercent: number;
    dailyAvg: number;
    /** Dias de cobertura do estoque no ritmo atual (null = sem vendas). */
    daysOfStock: number | null;
  }[];
  /** Giro agregado por tamanho (opção "Tamanho" das variações). */
  bySize: { size: string; soldQty: number; stock: number; sellThroughPercent: number }[];
  /** Reposição sugerida = venda média diária × 30 − estoque (arredondado para cima). */
  restock: {
    productId: string;
    variantId: string | null;
    name: string;
    variantName: string | null;
    soldQty: number;
    dailyAvg: number;
    stock: number;
    suggestedQty: number;
  }[];
}

/** Categoria sintética usada no resumo financeiro para as taxas de maquininha/meios de pagamento (feeCents). */
export const PAYMENT_FEES_CATEGORY = { name: 'Taxas de pagamento', color: '#0891B2' } as const;
